import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import barbersLogo from '../../images/barbers-limited-text-logo.png';
import { accent } from './darkFormStyles';
import { Link as RouterLink } from 'react-router-dom';

export interface StickyFooterProp {
  companyLinkName: string;
}

const linkSx = {
  color: 'rgba(0,0,0,0.65)',
  textDecoration: 'none',
  fontSize: '0.9rem',
  transition: 'color 0.2s',
  '&:hover': { color: accent },
};

const socialLinks = [
  {
    icon: <InstagramIcon fontSize="small" />,
    label: 'Instagram',
    href: 'https://www.instagram.com/barbers.limited/',
  },
  {
    icon: <FacebookIcon fontSize="small" />,
    label: 'Facebook',
    href: 'https://www.facebook.com/profile.php?id=61594808369546',
  },
];

export default function StickyFooter({ companyLinkName }: StickyFooterProp) {
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: '#fff',
        color: '#000',
        borderTop: `3px solid ${accent}`,
        mt: 'auto',
        width: '100%',
      }}
      id={'footer-box'}
    >
      <Container maxWidth="lg" sx={{ py: 6, px: { xs: 3, sm: 4 } }}>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={4}>
            <Box
              component="img"
              alt="Barbers Limited"
              src={barbersLogo}
              className="sticky-image"
              sx={{ width: 150, height: 'auto', mb: 2 }}
            />
            <Typography
              variant="body2"
              sx={{ color: 'rgba(0,0,0,0.6)', maxWidth: 280 }}
            >
              Book, manage, and grow with Barbers Limited — the platform for
              barbers and shop owners.
            </Typography>
          </Grid>

          <Grid item xs={6} sm={2.5}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ color: '#000', mb: 1.5 }}
            >
              Legal
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link component={RouterLink} to="/privacy-policy" sx={linkSx}>
                Privacy Policy
              </Link>
              <Link component={RouterLink} to="/terms-conditions" sx={linkSx}>
                Terms & Conditions
              </Link>
            </Box>
          </Grid>

          <Grid item xs={6} sm={2.5}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ color: '#000', mb: 1.5 }}
            >
              Support
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link component={RouterLink} to="/help-support" sx={linkSx}>
                Help & Support
              </Link>
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ color: '#000', mb: 1.5 }}
            >
              Follow Us
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {socialLinks.map((s) => (
                <IconButton
                  key={s.label}
                  component="a"
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  size="small"
                  sx={{
                    color: '#000',
                    border: '1px solid rgba(0,0,0,0.15)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: '#fff',
                      bgcolor: accent,
                      borderColor: accent,
                    },
                  }}
                >
                  {s.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: 'rgba(0,0,0,0.1)' }} />

        <Typography
          variant="body2"
          sx={{ textAlign: 'center', color: 'rgba(0,0,0,0.55)' }}
        >
          <Link
            href="https://barbers.limited/"
            sx={{
              color: accent,
              fontWeight: 'bold',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {companyLinkName}
          </Link>{' '}
          {'© '} {new Date().getFullYear()} All Rights Reserved
        </Typography>
      </Container>
    </Box>
  );
}
